import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getPackages } from "../../services/package";
import { selectSkyPointPlan } from "../../services/subscription";
import { getCookie, performLogout } from "../../utils";
import { pageInfoAnalytics } from "../../config/mixpanelConfig";
import styles from "./SkyPointPackages.module.css";
import cardStyles from "./PricingCard.module.css";

const PricingCard = ({ plan, isYearly, onClick, disabled }) => {
  const pricing = isYearly ? plan?.yearly : plan?.monthly;

  return (
    <div className={`${cardStyles.card} ${plan?.isPopular ? cardStyles["card--popular"] : ""}`}>
      {plan?.isPopular && <div className={cardStyles.card__badge}>Most Popular</div>}
      <div className={cardStyles.card__header}>
        <p className={cardStyles.card__name}>{plan?.name}</p>
        <p className={cardStyles.card__description}>{plan?.description}</p>
      </div>
      <div className={cardStyles.card__body}>
        <div className={cardStyles.card__pricing}>
          <p className={cardStyles.card__price}>{pricing?.price}</p>
          <p className={cardStyles.card__period}>{pricing?.period}</p>
        </div>
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          className={`${cardStyles.card__button} ${cardStyles[`card__button--${plan?.buttonVariant}`]}`}
        >
          {plan?.buttonText}
        </button>
        <div className={cardStyles.card__divider}></div>
        <ul className={cardStyles.card__features}>
          {plan?.features.map((feature, index) => (
            <li key={index} className={cardStyles.card__feature}>
              <span className={cardStyles.card__featureIcon}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="10" cy="10" r="10" fill="#EBEBEB" />
                  <path
                    d="M14.6666 6.66663L8.24992 13.0833L5.33325 10.1666"
                    stroke="black"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className={cardStyles.card__featureText}>{feature}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export const SkyPointPackages = () => {
  const { profile } = useSelector((state) => state.profile);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [packages, setPackages] = useState([]);
  const [isYearly, setIsYearly] = useState(false);
  const navigate = useNavigate();
  const billingType = isYearly ? "year" : "month";

  const skypointPackages = packages?.filter(({ type }) => type === "skypoint");
  const displayPackages = skypointPackages
    ?.filter((item) => item?.duration === billingType)
    ?.map(mapPackageToPlan);

  function mapPackageToPlan(pkg) {
    const cost = Number(pkg.cost);
    const name = pkg.name?.replace(/ - Yearly$/, "");

    const features = [];
    features.push(`${pkg.staff} Staff Members`);
    // features.push("Timesheet Management");
    // features.push("Support Plan Management");
    // features.push("Document Management");
    // features.push("Staff Attendance");
    // features.push("Billing & Invoices");
    // features.push("Reports & Analytics");
    // features.push("Notifications");

    const isPopular = name === "Professional";
    const buttonVariant = isPopular ? "yellow" : "black";

    return {
      name,
      description: getDescription(name),
      monthly: {
        price: `$${cost}`,
        period: "/month",
      },
      yearly: {
        price: `$${cost}`,
        period: "/year",
      },
      features,
      buttonText: "Get Started",
      buttonVariant,
      isPopular,
      _id: pkg.id,
      _raw: pkg,
    };
  }

  function getDescription(name) {
    const descriptions = {
      Starter: "Entry level for small providers.",
      Professional: "Perfect for growing agencies.",
      Enterprise: "Comprehensive solution for large teams.",
    };
    return descriptions[name] || "For disability support providers.";
  }

  const getAllPackages = async () => {
    try {
      const data = await getPackages(profile?.user?.id);
      if (data) {
        const sorted = data?.packages
        ?.filter(pkg => Number(pkg.cost) === 0)
        ?.sort((a, b) => Number(a.cost) - Number(b.cost));
        setPackages(sorted || []);
      }
    } catch (err) {
      console.error("Failed to load packages:", err);
    } finally {
      setLoadingPackages(false);
    }
  };

  const handleSelectPackage = async (id) => {
    setIsLoading(true);
    const resp = await selectSkyPointPlan({ id });
    if (resp) setIsLoading(false);
    if (resp?.data?.package) {
      const pkgName = resp?.data?.package?.name;
      navigate("/payInfo", {
        state: {
          stripePlan: resp?.data?.package?.stripeId,
          planId: resp?.data?.package?.id,
          plan: resp?.data?.package,
          monthlyPlan: skypointPackages?.find(
            (item) => item?.name === pkgName && item?.duration === "month"
          ),
          yearlyPlan: skypointPackages?.find(
            (item) => item?.name === pkgName && item?.duration === "year"
          ),
          taxDetails: resp?.data?.taxDetails,
        },
      });
    }
  };

  const handleLogout = () => {
    performLogout("/");
  };

  useEffect(() => {
    getAllPackages();
    pageInfoAnalytics("Skypoint Packages Page Viewed", "Skypoint Packages Page");
  }, []);

  return (
    <div className={styles.page}>
      {/* Top bar */}
      <div className={styles.topBar}>
        <img src="/assets/images/skypoint-logo2.png" alt="Skypoint" className={styles.logoImg} />
        <button type="button" className={styles.logoutButton} onClick={handleLogout}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          Logout
        </button>
      </div>

      {/* Hero */}
      <div className={styles.hero}>
        <div className={styles.header}>
          <h1 className={styles.title}>Select a plan</h1>
          <p className={styles.subtitle}>Choose the package that suits your needs</p>
        </div>
        {/* <div className={styles.toggle}>
          <div className={styles.toggleWrapper}>
            <div className={`${styles.toggleBg} ${isYearly ? styles.toggleBgRight : ""}`} />
            <button
              type="button"
              className={`${styles.toggleButton} ${!isYearly ? styles.toggleButtonActive : ""}`}
              onClick={() => setIsYearly(false)}
            >
              Monthly
            </button>
            <button
              type="button"
              className={`${styles.toggleButton} ${isYearly ? styles.toggleButtonActive : ""}`}
              onClick={() => setIsYearly(true)}
            >
              Yearly <span className={styles.toggleDiscount}>(10% off)</span>
            </button>
          </div>
        </div> */}
      </div>

      {/* Cards */}
      <div className={styles.cardsSection}>
        <div className={styles.container}>
          {loadingPackages ? (
            <div className={styles.loadingGrid}>
              {[1, 2, 3].map((i) => (
                <div key={i} className={styles.skeleton} />
              ))}
            </div>
          ) : displayPackages?.length === 0 ? (
            <p style={{ textAlign: "center", color: "#6b7280", marginTop: 60 }}>
              No packages available.
            </p>
          ) : (
            <div className={styles.grid}>
              {displayPackages?.map((plan) => (
                <PricingCard
                  key={plan.name}
                  plan={plan}
                  isYearly={isYearly}
                  onClick={() => handleSelectPackage(plan._id)}
                  disabled={isLoading}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
